import { Button, Flex, List, Stack, Text } from '@mantine/core';
import { ConfirmationModalV3 } from '@dealstack/frontend/shared/components/ConfirmationModal/ConfirmationModalV3.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import type { InseparableRedemptionDisclosure } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/CreateNewTransactionDrawer/inseparableRedemption.utils.ts';

interface Props {
	disclosure: InseparableRedemptionDisclosure;
	isSubmitting: boolean;
	onProceed: () => void;
	onCancel: () => void;
	/**
	 * CAP-2035: the transfer path reuses this dialog so both nominee disclosures read alike, but the
	 * action is not a redemption. Defaults to the redemption wording.
	 */
	proceedLabel?: string;
}

export const InseparableRedemptionModal = ({
	disclosure,
	isSubmitting,
	onProceed,
	onCancel,
	proceedLabel,
}: Props) => {
	const tCapManage = useTranslationCapManage();

	return (
		<ConfirmationModalV3
			title={disclosure.title}
			state="info"
			description={
				<Stack gap="xs">
					<Text size="sm">{disclosure.intro}</Text>
					<List size="sm" withPadding>
						{disclosure.bullets.map((bullet) => (
							<List.Item key={bullet}>{bullet}</List.Item>
						))}
					</List>
					{disclosure.partitionWarning ? (
						<Text size="sm" c="orange.7">
							{disclosure.partitionWarning}
						</Text>
					) : null}
				</Stack>
			}
			children={
				<Flex gap="xs">
					<Button
						size="md"
						variant="secondary"
						onClick={onCancel}
						disabled={isSubmitting}
					>
						{tCapManage('transactions.inseparable_redemption.cancel')}
					</Button>
					<Button
						size="md"
						variant="subtle"
						loading={isSubmitting}
						data-testid={
							TEST_ELEMENT_IDS.CAP_TRANSACTION_INSEPARABLE_REDEMPTION_PROCEED
						}
						onClick={onProceed}
					>
						{proceedLabel ?? tCapManage('transactions.inseparable_redemption.proceed')}
					</Button>
				</Flex>
			}
		/>
	);
};
